import {
  ChartIcon,
  AddIcon,
  EditIcon,
  LineChart,
  PieChart,
  DonutChart,
  DeleteCol,
  DeleteRow,
  CloseIcon,
} from "../../icons/icons";
import colorPicker, { toRGBA } from "./utils/colorPicker";
import { make } from "./utils/dom";
import { Chart } from "chart.js/auto";
import BtnTooltip from "../../tooltips/btnTooltip";
import interact from "interactjs";

export default class ChartBlock {
  static get toolbox() {
    return {
      title: "Chart",
      icon: ChartIcon,
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this._settings = config;
    this.block = block;

    this._CSS = {
      // chart table
      chartTableContainer: "chart-table-container",
      chartTableRedactor: "chart-table-redactor",
      chartTable: "chart-table",
      chartTableRow: "chart-table-row",
      chartTableCell: "chart-table-cell",
      chartTableInput: "chart-table-input",
      chartTableAddRowBtn: "chart-table-add-row",
      chartTableAddColBtn: "chart-table-add-col",
      chartTableCloseBtn: "chart-table-close-btn",
      chartColorPickerBtn: "chart-color-picker-btn",
      chartColColorBtn: "chart-column-color-btn",
      chartRowColorBtn: "chart-row-color-btn",
      chartDeleteConfirm: "chart-delete-confirm",
      chartDeleteCancel: "chart-delete-cancel",
      deleteDialogBackdrop: "chart-delete-dialog-backdrop",
      deleteDialog: "chart-delete-dialog",
      deleteDialogMessage: "chart-delete-dialog-message",

      chartToolbarContainer: "cdx-chart-toolbar-container",
      csvFileInput: "csv-file-input",
      chartEditBtnContainer: "chart-edit-btn-container",
      // standard css
      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      save: "save",
      clear: "clear",
      cancel: "cancel",
      focusVisibleBtn: "focus-visible-btn",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      tuneButton: "editor-block-tune-button",
      editorFont: "editor-font",
      editorBlockToolbar: "editor-block-toolbar",
      editorBlockModalBackdrop: "editor-block-modal-backdrop",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",
    };
    this._data = {
      type: data.type || "bar",
      datasetColors: data.datasetColors || ["", "", "", "", ""],
      chartData: data.chartData || [
        {
          seriesColor: "",
          dataset: ["Chart title", "data 1", "data 2", "data 3", "data 4"],
        },
        { seriesColor: "", dataset: ["dataset 1", "12", "80", "76", "21"] },
      ],
    };

    this.table = this.getTable();
    this.toolbar = this.getToolbar();
    this.canvas = make("canvas", "chart-block-canvas");
    this.debouncedRenderChart = this.debounce(() => {
      this.renderChart(this._data.type);
    }, 300);
    this.colorPicker = colorPicker(this._settings.options.swatches, () =>
      this.renderChart(this._data.type),
    );
    this.activeCell = null;
    this.activeRow = null;
  }

  getToolbar() {
    const css = this._CSS;
    const toolbarContainer = make("div", [css.chartToolbarContainer]);
    const deleteRowBtn = make("button", [
      css.blockBtn,
      css.xxlIcon,
      css.blockBtnSmall,
      css.clear,
    ]);
    const deleteColBtn = make("button", [
      css.blockBtn,
      css.xxlIcon,
      css.blockBtnSmall,
      css.clear,
    ]);
    deleteRowBtn.innerHTML = DeleteRow;
    deleteColBtn.innerHTML = DeleteCol;

    const makeBtn = (icon, type, tooltip) => {
      const toolbarBtn = make("button", [
        css.blockBtn,
        css.xxlIcon,
        css.blockBtnSmall,
      ]);

      toolbarBtn.addEventListener("click", () => {
        this._data.type = type;
        this.table.setAttribute("data-chart-type", type);
        this.renderChart(type);
      });

      BtnTooltip(toolbarBtn, tooltip);
      toolbarBtn.innerHTML = icon;
      toolbarContainer.appendChild(toolbarBtn);
    };

    const closeBtn = make("button", [
      css.chartTableCloseBtn,
      css.blockBtn,
      css.blockBtnSmall,
      css.clear,
    ]);

    closeBtn.innerHTML = CloseIcon;

    makeBtn(ChartIcon, "bar", "Bar chart");
    makeBtn(LineChart, "line", "Line chart");
    makeBtn(PieChart, "pie", "Pie chart");
    makeBtn(DonutChart, "doughnut", "Doughnut chart");

    deleteRowBtn.addEventListener("click", () => {
      const rowLength = this.table.querySelectorAll(
        `.${css.chartTableRow}`,
      ).length;
      if (this.activeRow && rowLength > 1) {
        this.activeRow.classList.add("delete-queue");
        this.deleteConfirmation(this.table, "row", () => {
          const tableBody = this.table.querySelector("tbody");
          tableBody.removeChild(this.activeRow);
          this.block.dispatchChange();
          this.debouncedRenderChart();
        });
      }
    });

    deleteColBtn.addEventListener("click", () => {
      const css = this._CSS;
      const colLength = this.table
        .querySelector(`.${css.chartTableRow}`)
        .querySelectorAll(`.${css.chartTableCell}`).length;
      if (this.activeCell && colLength > 1) {
        const cellIndex = Array.from(
          this.activeCell.parentElement.children,
        ).indexOf(this.activeCell);
        const rows = this.table.querySelectorAll(`.${css.chartTableRow}`);
        rows.forEach((row) => {
          const cells = row.querySelectorAll(`.${css.chartTableCell}`);
          if (cells[cellIndex]) {
            cells[cellIndex].classList.add("delete-queue");
          }
        });

        this.deleteConfirmation(this.table, "column", () => {
          rows.forEach((row) => {
            const cells = row.querySelectorAll(`.${css.chartTableCell}`);
            if (cells[cellIndex]) {
              row.removeChild(cells[cellIndex]);
            }
          });
          this.block.dispatchChange();
          this.debouncedRenderChart();
        });
      }
    });

    closeBtn.addEventListener("click", () => {
      this.table.classList.remove("visible");
      this.table.dataset.x = 342;
      this.table.dataset.y = 250;
      this.table.style.top = "250px";
      this.table.style.left = "342px";
    });

    BtnTooltip(deleteRowBtn, "Delete row");
    BtnTooltip(deleteColBtn, "Delete column");

    toolbarContainer.appendChild(deleteRowBtn);
    toolbarContainer.appendChild(deleteColBtn);
    toolbarContainer.appendChild(closeBtn);

    return toolbarContainer;
  }

  getTable() {
    const css = this._CSS;
    const tableContainer = make("div", css.chartTableContainer);
    const tableRedactor = make("div", css.chartTableRedactor);

    const table = make("table", css.chartTable);
    const tableBody = make("tbody");

    // Attach tbody ONCE
    table.appendChild(tableBody);

    const { chartData, datasetColors } = this._data;

    const getRandomSwatchColor = () => {
      const swatches = this._settings.options.swatches;

      const excluded = new Set(["#000000", "#ffffff", "#00000000"]);

      const validSwatches = swatches.filter(
        (c) => !excluded.has(c.toLowerCase()),
      );

      if (validSwatches.length === 0) {
        return "rgba(0,0,0,0.3)";
      }

      const randomSwatch =
        validSwatches[Math.floor(Math.random() * validSwatches.length)];

      const alpha = Math.random() * (1 - 0.3) + 0.3;

      return `rgba(${toRGBA(randomSwatch, alpha).join(",")})`;
    };

    const addRow = (cellLength, currentCellIndex) => {
      const row = make("tr", css.chartTableRow);

      for (let i = 0; i < cellLength; i++) {
        const cell = make("td", css.chartTableCell);
        const input = make("input", css.chartTableInput);
        input.type = "text";

        if (i === 0) {
          const dataColor = getRandomSwatchColor();
          const colorBtn = make("button", [
            css.chartRowColorBtn,
            css.chartColorPickerBtn,
          ]);
          addColorBtnListener(colorBtn);

          BtnTooltip(colorBtn, "Dataset color");
          cell.setAttribute("data-color", dataColor);
          colorBtn.style.backgroundColor = dataColor;

          cell.appendChild(colorBtn);
        }

        cell.appendChild(input);
        row.appendChild(cell);
        addInputListeners(input);
      }

      tableBody.appendChild(row);

      if (currentCellIndex !== undefined) {
        const rows = tableBody.querySelectorAll(`.${css.chartTableRow}`);
        const lastRow = rows[rows.length - 1];
        const targetInput = lastRow
          .querySelectorAll(`.${css.chartTableCell}`)
          [currentCellIndex]?.querySelector(`.${css.chartTableInput}`);
        targetInput?.focus();
      }
    };

    const addColumn = (currentRowIndex, currentCellIndex) => {
      const rows = tableBody.querySelectorAll(`.${css.chartTableRow}`);

      rows.forEach((row, rowIndex) => {
        const cell = make("td", css.chartTableCell);
        const input = make("input", css.chartTableInput);
        input.type = "text";

        if (rowIndex === 0) {
          const dataColor = getRandomSwatchColor();

          const colorBtn = make("button", [
            css.chartColColorBtn,
            css.chartColorPickerBtn,
          ]);
          addColorBtnListener(colorBtn);
          BtnTooltip(colorBtn, "Data color");
          cell.setAttribute("data-color", dataColor);
          colorBtn.style.backgroundColor = dataColor;
          cell.appendChild(colorBtn);
        }

        cell.appendChild(input);
        row.appendChild(cell);
        addInputListeners(input);
      });

      if (currentRowIndex !== undefined) {
        const rows = tableBody.querySelectorAll(`.${css.chartTableRow}`);
        const cellIndex =
          rows[0].querySelectorAll(`.${css.chartTableCell}`).length - 1;

        const targetInput = rows[currentRowIndex]
          ?.querySelectorAll(`.${css.chartTableCell}`)
          [cellIndex]?.querySelector(`.${css.chartTableInput}`);

        targetInput?.focus();
      }
    };

    const addInputListeners = (input) => {
      input.addEventListener("keydown", (e) => {
        const currentRow = e.target.closest(`.${css.chartTableRow}`);
        const rows = tableBody.querySelectorAll(`.${css.chartTableRow}`);
        const currentRowIndex = Array.from(rows).indexOf(currentRow);
        const currentCellIndex = Array.from(currentRow.children).indexOf(
          e.target.closest(`.${css.chartTableCell}`),
        );

        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          const isLastRow = currentRowIndex === rows.length - 1;

          if (isLastRow) {
            addRow(currentRow.children.length, currentCellIndex);
          } else {
            rows[currentRowIndex + 1]?.children[currentCellIndex]
              ?.querySelector(`.${css.chartTableInput}`)
              ?.focus();
          }
        }

        if (e.key === "Enter" && e.shiftKey && currentRowIndex > 0) {
          e.preventDefault();
          rows[currentRowIndex - 1]?.children[currentCellIndex]
            ?.querySelector(`.${css.chartTableInput}`)
            ?.focus();
        }

        if (e.key === "Tab" && !e.shiftKey) {
          e.preventDefault();
          const isLastCell =
            currentCellIndex === currentRow.children.length - 1;
          if (isLastCell) {
            addColumn(currentRowIndex, currentCellIndex);
          } else {
            currentRow.children[currentCellIndex + 1]
              ?.querySelector(`.${css.chartTableInput}`)
              ?.focus();
          }
        }

        if (e.key === "Tab" && e.shiftKey && currentCellIndex > 0) {
          e.preventDefault();
          currentRow.children[currentCellIndex - 1]
            ?.querySelector(`.${css.chartTableInput}`)
            ?.focus();
        }
      });

      input.addEventListener("input", () => {
        this.block.dispatchChange();
        this.debouncedRenderChart();
      });
      input.addEventListener("focus", () => {
        this.activeCell = input.closest(`.${css.chartTableCell}`);
        this.activeRow = input.closest(`.${css.chartTableRow}`);
        this.activeCell.classList.add("focused-cell");
        this.activeRow.classList.add("focused-row");
      });
      input.addEventListener("blur", () => {
        if (this.activeCell) {
          this.activeCell.classList.remove("focused-cell");
        }
        if (this.activeRow) {
          this.activeRow.classList.remove("focused-row");
        }
      });
    };

    const addColorBtnListener = (btn) => {
      btn.addEventListener("click", () => {
        const cell = btn.closest(`.${css.chartTableCell}`);
        // sets initial opacity slider value
        if (
          cell.getAttribute("data-color") !== "" &&
          cell.getAttribute("data-color") !== null
        ) {
          const rgbaArray = cell
            .getAttribute("data-color")
            .replace(/^rgba?\(|\)$/g, "")
            .split(",")
            .map((v) => Number(v.trim()));
          this.colorPicker.setOpacity(rgbaArray[3]);
        } else {
          this.colorPicker.setOpacity(1);
        }

        // Set color picker visibility
        if (
          this.colorPicker.main.classList.contains("visible") &&
          this.colorPicker.getTarget() === cell
        ) {
          this.colorPicker.main.classList.remove("visible");
        } else {
          this.colorPicker.main.classList.add("visible");
          window.addEventListener("pointerdown", pickerDismiss, true);
        }

        // reposition and re-target color picker
        const btnRect = btn.getBoundingClientRect();
        const tableContainerRect = cell
          .closest(".chart-table-container")
          .getBoundingClientRect();
        this.colorPicker.setTarget(cell);
        this.colorPicker.main.style.left = `${
          btnRect.left - tableContainerRect.left
        }px`;
        this.colorPicker.main.style.top = `${
          btnRect.top -
          tableContainerRect.top -
          this.colorPicker.main.getBoundingClientRect().height
        }px`;
      });
    };

    const pickerDismiss = (e) => {
      if (!this.colorPicker.main.contains(e.target)) {
        this.colorPicker.main.classList.remove("visible");
        window.removeEventListener("pointerdown", pickerDismiss, true);
      }
    };

    const renderTable = (chartData) => {
      chartData.forEach((rowData, rowIndex) => {
        const row = make("tr", css.chartTableRow);
        const dataset = rowData.dataset.length ? rowData.dataset : [""];

        dataset.forEach((cellData, cellIndex) => {
          const cell = make("td", css.chartTableCell);
          const input = make("input", css.chartTableInput);
          input.type = "text";
          input.value = cellData;

          addInputListeners(input);

          if (rowIndex === 0 && cellIndex > 0) {
            const colorBtn = make("button", [
              css.chartColColorBtn,
              css.chartColorPickerBtn,
            ]);
            addColorBtnListener(colorBtn);
            const columnColor =
              datasetColors[cellIndex - 1] || getRandomSwatchColor();
            colorBtn.style.backgroundColor = columnColor;

            BtnTooltip(colorBtn, "Data color");

            cell.dataset.color = columnColor;
            cell.appendChild(colorBtn);
          }

          if (rowIndex > 0 && cellIndex === 0) {
            const colorBtn = make("button", [
              css.chartRowColorBtn,
              css.chartColorPickerBtn,
            ]);
            addColorBtnListener(colorBtn);
            const rowColor = rowData.seriesColor || getRandomSwatchColor();
            colorBtn.style.backgroundColor = rowColor;

            BtnTooltip(colorBtn, "Dataset color");

            cell.dataset.color = rowColor;
            cell.appendChild(colorBtn);
          }

          cell.appendChild(input);
          row.appendChild(cell);
        });

        tableBody.appendChild(row);
      });
    };

    renderTable(chartData);

    tableRedactor.appendChild(table);

    if (!this.readOnly) {
      const addRowBtn = make("button", [
        css.chartTableAddRowBtn,
        css.blockBtn,
        css.blockBtnSmall,
        css.xlIcon,
      ]);

      const addColBtn = make("button", [
        css.chartTableAddColBtn,
        css.blockBtn,
        css.blockBtnSmall,
        css.xlIcon,
      ]);

      addRowBtn.innerHTML = AddIcon;
      addColBtn.innerHTML = AddIcon;

      addRowBtn.addEventListener("click", () => {
        const cellLength = tableBody
          .querySelector(`.${css.chartTableRow}`)
          .querySelectorAll(`.${css.chartTableCell}`).length;
        addRow(cellLength);
      });

      addColBtn.addEventListener("click", () => addColumn());

      tableContainer.appendChild(this.getToolbar());
      tableContainer.appendChild(tableRedactor);
      tableContainer.appendChild(addColBtn);
      tableContainer.appendChild(addRowBtn);
    }

    interact(tableContainer)
      .draggable({
        inertia: true,
        ignoreFrom: ".picker-main, .chart-table-input, .chart-delete-dialog",
        modifiers: [
          interact.modifiers.restrictRect({
            restriction: tableContainer.closest(".slide"),
            endOnly: true,
          }),
        ],

        listeners: {
          move(event) {
            const target = event.target;

            const x = (parseFloat(target.dataset.x) || 342) + event.dx;
            const y = (parseFloat(target.dataset.y) || 250) + event.dy;

            target.style.position = "fixed";
            target.style.top = `${y}px`;
            target.style.left = `${x}px`;
            target.dataset.x = x;
            target.dataset.y = y;
          },
        },
      })
      .resizable({
        edges: { left: true, right: true, bottom: true, top: true },

        inertia: true,

        modifiers: [
          // keep resize within slide
          interact.modifiers.restrictEdges({
            outer: tableContainer.closest(".slide"),
          }),

          // // minimum size
          // interact.modifiers.restrictSize({
          //   min: { width: 300, height: 200 },
          // }),
        ],

        listeners: {
          move(event) {
            const target = event.target;

            let x = parseFloat(target.dataset.x) || 342;
            let y = parseFloat(target.dataset.y) || 250;

            // update size
            target.style.width = `${event.rect.width}px`;
            target.style.height = `${event.rect.height}px`;

            // translate when resizing from top/left
            x += event.deltaRect.left;
            y += event.deltaRect.top;

            target.style.left = `${x}px`;
            target.style.top = `${y}px`;

            target.dataset.x = x;
            target.dataset.y = y;
          },
        },
      });

    return tableContainer;
  }

  getCsvBtn() {
    const css = this._CSS;
    const importCsvBtn = make("div");
    const importCsvBtnLabel = make("label", [
      css.csvFileInput,
      css.blockBtn,
      css.xxlIcon,
      css.blockBtnLarge,
      css.focusVisibleBtn,
    ]);
    const importCsvInput = make("input");
    importCsvInput.type = "file";
    importCsvInput.setAttribute("accept", ".csv");

    importCsvBtnLabel.innerText = "Import CSV";
    importCsvBtnLabel.appendChild(importCsvInput);
    importCsvBtn.appendChild(importCsvBtnLabel);

    function parseCSV(csvText) {
      const rows = [];
      let row = [];
      let value = "";
      let inQuotes = false;

      for (let i = 0; i < csvText.length; i++) {
        const char = csvText[i];
        const nextChar = csvText[i + 1];

        // Handle quotes
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            value += '"'; // escaped quote
            i++;
          } else {
            inQuotes = !inQuotes;
          }
          continue;
        }

        // Handle commas
        if (char === "," && !inQuotes) {
          row.push(value);
          value = "";
          continue;
        }

        // Handle newlines
        if ((char === "\n" || char === "\r") && !inQuotes) {
          if (char === "\r" && nextChar === "\n") i++; // Windows line endings
          row.push(value);
          rows.push(row);
          row = [];
          value = "";
          continue;
        }

        value += char;
      }

      // Push last value
      row.push(value);
      rows.push(row);

      return rows;
    }

    importCsvInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = (event) => {
        const csvText = event.target.result;
        const parsedData = parseCSV(csvText);

        this.processCsvData(parsedData);
      };

      reader.readAsText(file);
    });
    return importCsvBtn;
  }

  deleteConfirmation(table, type = "row", callbackFn) {
    const css = this._CSS;
    const dialogBackDrop = make("div", css.deleteDialogBackdrop);
    const dialog = make("div", css.deleteDialog);
    const confirmBtn = make("button", [
      css.blockBtn,
      css.blockBtnLarge,
      css.deleteConfirm,
      css.clear,
    ]);
    const cancelBtn = make("button", [
      css.blockBtn,
      css.blockBtnLarge,
      css.deleteCancel,
    ]);
    const message = make("div", css.deleteDialogMessage);
    message.innerText = `Delete ${type}?`;
    confirmBtn.innerText = "Delete";
    cancelBtn.innerText = "Cancel";
    dialog.appendChild(message);
    dialog.appendChild(confirmBtn);
    dialog.appendChild(cancelBtn);

    confirmBtn.addEventListener("click", () => {
      callbackFn();
      table.removeChild(dialogBackDrop);
    });

    cancelBtn.addEventListener("click", () => {
      const allDeleteQueues = table.querySelectorAll(".delete-queue");
      allDeleteQueues.forEach((el) => el.classList.remove("delete-queue"));
      table.removeChild(dialogBackDrop);
    });

    interact(dialog).draggable({
      inertia: true,
      modifiers: [
        interact.modifiers.restrictRect({
          restriction: dialog.closest(".slide"),
          endOnly: true,
        }),
      ],

      listeners: {
        start(event) {
          const target = event.target;
          const parent = target.parentElement;

          const parentRect = parent.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();

          // initial position in PX
          const xPx = targetRect.left - parentRect.left;
          const yPx = targetRect.top - parentRect.top;

          // convert to %
          const xPercent = (xPx / parentRect.width) * 100;
          const yPercent = (yPx / parentRect.height) * 100;

          target.dataset.x = xPercent;
          target.dataset.y = yPercent;
        },

        move(event) {
          const target = event.target;
          const parent = target.parentElement;

          const parentRect = parent.getBoundingClientRect();

          // current % → px
          let xPx = (parseFloat(target.dataset.x) / 100) * parentRect.width;
          let yPx = (parseFloat(target.dataset.y) / 100) * parentRect.height;

          // apply movement in px
          xPx += event.dx;
          yPx += event.dy;

          // convert back to %
          const xPercent = (xPx / parentRect.width) * 100;
          const yPercent = (yPx / parentRect.height) * 100;

          target.style.position = "absolute";
          target.style.left = `${xPercent}%`;
          target.style.top = `${yPercent}%`;

          target.dataset.x = xPercent;
          target.dataset.y = yPercent;
        },
      },
    });

    dialogBackDrop.appendChild(dialog);
    table.appendChild(dialogBackDrop);
  }

  render() {
    const css = this._CSS;
    this.container = make("div", "chart-block-wrapper");
    this.container.appendChild(this.canvas);

    // hidden input hack
    // prevents empty blocks being created
    const hiddenInput = document.createElement("input");
    hiddenInput.setAttribute(
      "style",
      "visibility: hidden; width: 0; height: 0; padding: 0; margin: 0; border: 0; position: absolute",
    );
    this.container.appendChild(hiddenInput);

    this.table.setAttribute("data-chart-type", this._data.type);
    const editBtnContainer = make("div");
    editBtnContainer.classList.add(css.chartEditBtnContainer);

    if (!this.readOnly) {
      // hidden input hack
      // prevents empty blocks being created
      let input = document.createElement("input");
      input.setAttribute(
        "style",
        "visibility: hidden; width: 0; height: 0; padding: 0; margin: 0; border: 0; position: absolute",
      );

      this.container.appendChild(input);

      this.editBtn = make("button", [
        css.blockBtn,
        css.xxlIcon,
        css.blockBtnSmall,
        css.focusVisibleBtn,
      ]);
      this.editBtn.innerHTML = EditIcon;
      this.editBtn.addEventListener("click", () => {
        console.log("open editor",this.table);
        if (this.table.classList.contains("visible")) {
          this.table.classList.remove("visible");
          this.table.dataset.x = 342;
          this.table.dataset.y = 250;
          this.table.style.top = "250px";
          this.table.style.left = "342px";
        } else {
          this.table.classList.add("visible");
        }
      });
      editBtnContainer.appendChild(this.editBtn);
      editBtnContainer.appendChild(this.getCsvBtn());
    }

    // Redundancy to ensure chart renders on block duplication. rendered() only gets called on initial block creation, not on duplication, so this ensures the chart appears in both cases.
    this.renderChart(this._data.type);

    this.container.appendChild(editBtnContainer);
    return this.container;
  }

  rendered() {
    setTimeout(() => {
    this.renderChart(this._data.type);
    }, 200);

    if (!this.readOnly) {
      this.table.appendChild(this.colorPicker.main);
      this.slideContainer = this.container.closest(".slide") || this.container.closest("#post-body-edit-holder");
      this.slideContainer.appendChild(this.table);
    }

    this.mainContainer = this.container.closest(".ce-block");
    this.mainContainer.addEventListener("focusout", (event) => {
      // setTimeout(() => {
      //   if (!this.mainContainer.contains(document.activeElement)) {
      //     this.colorPicker.main.classList.remove("visible");
      //   }
      // }, 10);
    });
  }

  renderChart(type) {
    switch (type) {
      case "pie":
        this.renderPieChart();
        break;
      case "bar":
        this.renderBarChart();
        break;
      case "line":
        this.renderLineChart();
        break;
      case "doughnut":
        this.renderDonutChart();
        break;
      default:
        break;
    }
  }

  parseChartData(_chartData) {
    const labels = [];
    const datasets = [];
    _chartData.forEach((row, index) => {
      if (index === 0) {
        row.dataset.forEach((e, i) => {
          if (i === 0) return;
          labels.push(e);
        });
      } else {
        const data = [];
        row.dataset.forEach((f, j) => {
          if (j === 0) return;
          data.push(Number(f) || 0);
        });
        datasets.push({
          label: row.dataset[0],
          data,
          seriesColor: row.seriesColor,
        });
        return;
      }
    });
    return {
      labels,
      datasets,
    };
  }

  renderPieChart() {
    const { chartData, datasetColors } = this.processData();

    if (!chartData || chartData.length < 2) return;
    const { labels, datasets } = this.parseChartData(chartData);

    if (!this.canvas) return;

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }

    const ctx = this.canvas.getContext("2d");

    datasets.forEach((e, i) => {
      e.backgroundColor = datasetColors;
    });

    this.chartInstance = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
      },
    });
  }

  renderBarChart() {
    const { chartData } = this.processData();

    if (!chartData || chartData.length < 2) return;

    const { labels, datasets } = this.parseChartData(chartData);

    if (!this.canvas) return;

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
    const ctx = this.canvas.getContext("2d");

    datasets.forEach((e, i) => {
      e.backgroundColor = e.seriesColor;
    });

    this.chartInstance = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            position: "top",
          },
        },
      },
    });
  }

  renderLineChart() {
    const { chartData } = this.processData();

    if (!chartData || chartData.length < 2) return;

    const { labels, datasets } = this.parseChartData(chartData);

    if (!this.canvas) return;

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }

    const ctx = this.canvas.getContext("2d");

    datasets.forEach((e, i) => {
      e.backgroundColor = e.seriesColor;
      e.borderColor = e.seriesColor;
    });

    this.chartInstance = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            position: "top",
          },
        },
      },
    });
  }

  renderDonutChart() {
    const { chartData, datasetColors } = this.processData();

    if (!chartData || chartData.length < 2) return;

    const { labels, datasets } = this.parseChartData(chartData);

    if (!this.canvas) return;

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }

    const ctx = this.canvas.getContext("2d");

    datasets.forEach((e, i) => {
      e.backgroundColor = datasetColors;
    });

    this.chartInstance = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            position: "top",
          },
        },
      },
    });
  }

  processData() {
    const css = this._CSS;
    const datasetColors = [];
    const _chartData = [];
    const rows = this.table.querySelectorAll(`.${css.chartTableRow}`);
    rows.forEach((row, rowIndex) => {
      const rowData = { seriesColor: "", dataset: [] };
      const _dataset = [];
      const cells = row.querySelectorAll(`.${css.chartTableInput}`);
      cells.forEach((cell, cellIndex) => {
        if (rowIndex !== 0 && cellIndex === 0) {
          const seriesColor =
            cell
              .closest(`.${css.chartTableCell}`)
              ?.getAttribute("data-color") || "";
          rowData.seriesColor = seriesColor;
        } else if (rowIndex === 0 && cellIndex !== 0) {
          const dataColor =
            cell
              .closest(`.${css.chartTableCell}`)
              ?.getAttribute("data-color") || "";
          datasetColors.push(dataColor);
        }
        _dataset.push(cell.value || "");
      });
      rowData.dataset = _dataset;
      _chartData.push(rowData);
    });
    return {
      datasetColors: datasetColors,
      chartData: this.trimTable(_chartData),
    };
  }

  processCsvData(csvMatrix) {
    if (Array.isArray(csvMatrix) && csvMatrix.length > 0) {
      const newChartData = [];
      const previousChartData = this._data.chartData;
      csvMatrix.forEach((row, index) => {
        if (this._data.chartData.length > index) {
          // preserve previous colors if possible
          row[0] = previousChartData[index].dataset[0] || row[0];
          newChartData.push({
            seriesColor: previousChartData[index].seriesColor || "",
            dataset: [...row],
          });
          return;
        } else {
          newChartData.push({
            seriesColor: "",
            dataset: [...row],
          });
        }
      });
      this._data.chartData = this.trimTable(newChartData);
      const newTable = this.getTable();
      newTable.appendChild(this.colorPicker.main);
      const oldTable = this.table;
      oldTable.replaceWith(newTable);
      this.table = newTable;
      this.renderChart(this._data.type);
    }
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  trimTable(_data, minColumns = 5) {
    let globalLastIndex = -1;

    _data.forEach((row) => {
      for (let i = row.dataset.length - 1; i >= 0; i--) {
        if (row.dataset[i] !== "") {
          globalLastIndex = Math.max(globalLastIndex, i);
          break;
        }
      }
    });

    const finalLength = Math.max(globalLastIndex + 1, minColumns);

    return _data
      .map((row) => ({
        ...row,
        dataset: row.dataset.slice(0, finalLength),
      }))
      .filter((row) => row.dataset.some((cell) => cell !== ""));
  }

  destroy() {
    this.table.remove();
  }

  save() {
    const { datasetColors, chartData } = this.processData();
    this._data.datasetColors = datasetColors;
    this._data.chartData = chartData;
    return this._data;
  }
}
